/**
 * Continuous-tracking policy (single source of truth).
 *
 * TransportMate runs a continuous GPS watcher ONLY during an active
 * collection→delivery run (EN_ROUTE: POC completed, POD pending).
 *
 * Having a job merely assigned (ASSIGNED) does NOT justify a watcher: it kept
 * CLLocationManager alive for the whole working day and made iOS report the app
 * as constantly using location under "While Using the App".
 *
 * Features that need a single position (appraisal capture) use one-shot
 * getCurrentPosition and must never start a watcher.
 */
export type TrackingState = "IDLE" | "ASSIGNED" | "EN_ROUTE";

export function shouldRunContinuousTracking(params: {
  isMobileDevice: boolean;
  isDriverRecord: boolean | null;
  trackingState: TrackingState;
  hasSession?: boolean;
}): boolean {
  const { isMobileDevice, isDriverRecord, trackingState, hasSession = true } = params;
  if (!hasSession) return false;
  if (!isMobileDevice) return false;
  if (isDriverRecord !== true) return false;
  return trackingState === "EN_ROUTE";
}

/**
 * What an app resume / foreground transition may do to the native watcher.
 *
 * Resume must never act on stale knowledge: before 1.60.20 the resume handler
 * restarted a stale watcher purely because one had been started earlier
 * (`hasStartedRef`), without re-reading the policy. If POD/cancellation/sign-out
 * had just landed, that could revive a watcher the app was about to stop.
 *
 * Starting a watcher from cold is NOT a resume responsibility — the main
 * tracking effect owns that and reacts to the re-derived state. Resume only
 * decides whether an ALREADY running watcher must be torn down or refreshed.
 *
 *  - "stop"    → policy no longer allows tracking; tear the watcher down now
 *  - "restart" → policy still allows tracking but the watcher has gone stale
 *  - "none"    → leave the watcher exactly as it is
 */
export type ResumeTrackingAction = "stop" | "restart" | "none";

export function resolveResumeAction(params: {
  isMobileDevice: boolean;
  isDriverRecord: boolean | null;
  trackingState: TrackingState;
  hasSession?: boolean;
  /** Whether a native watcher is currently running on this device. */
  isTracking: boolean;
  /** Whether that watcher has stopped delivering fixes (existing staleness rule). */
  watcherStale: boolean;
}): ResumeTrackingAction {
  const { isTracking, watcherStale, ...policy } = params;
  if (!shouldRunContinuousTracking(policy)) {
    return isTracking ? "stop" : "none";
  }
  if (!isTracking) return "none";
  return watcherStale ? "restart" : "none";
}
